import React, { Component } from 'react';
import { isNil, isString, noop } from 'lodash';
import { ICellRendererParams } from '@ag-grid-community/core';

import { Overlay } from 'src/common-ui';
import container from 'src/ServiceContainer';
import { MasterDetailConfig } from 'src/utils/Component/AgGrid/AgConfigParse';
import { BasicTenantConfigViewItem } from 'src/dao/tenantConfigClient';
import { ASSORTMENT } from 'src/utils/Domain/Constants';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { AppType } from 'configuration-schemas/general';

import { MasterDetailChart } from 'src/components/MasterDetailRenderer/MasterDetailChart';
import { processApiParams } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import ServiceContainer from 'src/ServiceContainer';
import { ClientDataApi, isListDataApi } from 'configuration-schemas/confdefnView';
import EditableGrid, { EditableGridProps } from '../ConfigurableGrid/EditableGrid/EditableGrid';
import { ConfigurableGridColumnDef } from '../ConfigurableGrid/ConfigurableGrid.types';
import Axios from 'src/services/axios';
import { AddBreadcrumbPayload } from 'src/state/breadcrumbs/Bread.slice';
import { NavigateFunction } from 'react-router-dom';

export enum MasterDetailRendererType {
  chart = 'chart',
  editableGrid = 'editableGrid',
}

export interface MasterDetailChartMetrics {
  left: BasicTenantConfigViewItem[];
  right?: BasicTenantConfigViewItem[];
}

export interface MasterDetailChartConfig {
  chartTitle: string;
  renderer: MasterDetailRendererType.chart;
  xAxisDataIndex: string;
  metrics: MasterDetailChartMetrics;
}

export interface MasterDetailGridConfig
  extends Pick<EditableGridProps,
    'updateCoordinateMap'
    | 'massEditConfig'
    | 'idProp'
    | 'leafIdProp'
    | 'salesAdjustmentConfig'
    | 'clientActionHandlers'
    | 'onlySendCoordinateMap'
  > {
  renderer: MasterDetailRendererType.editableGrid;
  columns: ConfigurableGridColumnDef[];
}

export type MasterDetailConfigUnion = MasterDetailChartConfig | MasterDetailGridConfig;

interface MasterDetailRendererProps extends ICellRendererParams, MasterDetailConfig {
  addCrumb(payload: AddBreadcrumbPayload): void;
  routerNavigate: NavigateFunction;
 }

interface MasterDetailRendererState {
  isLoading: boolean;
  config: MasterDetailConfigUnion | null;
  data: BasicPivotItem[];
}

export class MasterDetailRenderer extends Component<MasterDetailRendererProps, MasterDetailRendererState> {
  constructor(props: MasterDetailRendererProps) {
    super(props);

    this.state = {
      isLoading: true,
      config: null,
      data: [],
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  async fetchData() {
    const configParams = this.props.configApi.params;
    const config = await container.tenantConfigClient.getTenantViewDefn<MasterDetailConfigUnion>({
      defnId: !isNil(configParams) ? configParams.defnId : '',
      appName: !isNil(configParams) ? (configParams.appName as AppType) : ASSORTMENT,
    });

    const dataApi = this.props.dataApi;
    const processedApi = processApiParams(dataApi, this.props.data);
    let data: BasicPivotItem[] = [];

    if (isListDataApi(processedApi)) {
      data = await ServiceContainer.pivotService
        .listData(processedApi.defnId, AppType.Assortment, processedApi.params)
        .then((resp) => {
          return resp.flat;
        });
    } else {
      const clientDataApi = dataApi as ClientDataApi;
      const { url: dataUrl, headers: dataHeaders } = clientDataApi;
      const dataOptions = {
        headers: dataHeaders,
        params: {
          ...processedApi.params
        },
      };
      data = await Axios.get(dataUrl, dataOptions)
        .then(async (resp) => {
          const dataResponse = resp.data;
          return isString(dataResponse) ? ServiceContainer.pivotService.deserialize(dataResponse) : dataResponse.list;
        })
        .then((formattedData: BasicPivotItem[]) => {
          // If we are dealing with a nested set, first level must be single item, gets second level
          // (/floorset returns [{channel: ..., children: [<row_records>]}])
          if (formattedData && formattedData[0] && formattedData[0].children && formattedData[0].children.length > 0) {
            return formattedData[0].children;
          } else {
            return formattedData;
          }
        });
    }

    this.setState({
      isLoading: false,
      config,
      data,
    });
  }

  render() {
    const { isLoading, config, data } = this.state;

    if (isLoading || isNil(config)) {
      return <Overlay visible={true} type={'loading'} fitParent={true} />;
    }

    const parentId = !isNil(this.props.dataApi.params) ? this.props.data[this.props.dataApi.params.style] : '';
    switch (config.renderer) {
      case MasterDetailRendererType.chart:
        return <MasterDetailChart parentId={parentId} config={config} data={data} />;
      case MasterDetailRendererType.editableGrid:
        const processedApi = processApiParams(this.props.dataApi, this.props.data);
        const anchorGridProps: EditableGridProps = {
          configLoaded: !isNil(config),
          dataLoaded: true,
          data: data,
          columnDefs: config.columns,
          updateCoordinateMap: config.updateCoordinateMap,
          massEditConfig: config.massEditConfig,
          idProp: config.idProp,
          leafIdProp: config.leafIdProp,
          salesAdjustmentConfig: config.salesAdjustmentConfig,
          clientActionHandlers: config.clientActionHandlers,
          onlySendCoordinateMap: config.onlySendCoordinateMap,
          handleGridReady: noop,
          dataApi: processedApi,
          addCrumb: this.props.addCrumb,
          routerNavigate: this.props.routerNavigate,
          // TODO: Not sure if we want to handle context around breadcrumbing
          onFirstDataRendered: noop,
        } as EditableGridProps;
        const margins: React.CSSProperties = {
          paddingTop: 15,
          paddingBottom: 15,
          paddingLeft: 30,
          paddingRight: 30,
        }
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%', ...margins, backgroundColor: "rgb(249, 249, 249)" }}>
          <div style={{ width: '100%',height: '100%', border: "1px solid grey" }}><EditableGrid {...anchorGridProps} /></div>
        </div>
      default:
        return <span>Unknown custom renderer for master detail.</span>;
    }
  }
}
